import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  IconButton, Alert, CircularProgress, MenuItem, TextField,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Checkbox, Collapse,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import ModuleHeader from './components/ModuleHeader';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  ArrowBack, Add, Assignment, Edit, Delete,
  Warning, FilterList, Close, Visibility, Print, Science,
  ExpandMore, ExpandLess,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';
import { dataParaSE } from './utils/semanaEpi';
import ConferenciaLaboratorio, { ResultadoConferencia, textoItens } from './ConferenciaLaboratorio';
import CampoEtiquetaTubitos from './CampoEtiquetaTubitos';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Agente { id: number; nome: string; matricula: string; status: string; }
interface Area   { id: number; nome: string; zona: string; ativa: boolean; }
interface Ciclo  { id: number; nome: string; ativo: boolean; ano?: number; numero?: number; }
interface CicloPe { id: number; numero: number; ano: number; ativo: boolean; encerrado: boolean; }

interface Fad01 {
  id: number; data: string; atividade: string; concluida: boolean;
  agente: Agente; area: Area; ciclo: Ciclo;
  quarteirao_texto: string | null; quarteirao_concluidos_texto: string | null;
  imoveis_residencia: number; imoveis_comercio: number;
  imoveis_terreno_baldio: number; imoveis_ponto_estrategico: number; imoveis_outros: number;
  trat_focal: number; trat_perifocal: number; inspecionados: number;
  recuperados: number; recusados: number; fechados: number;
  gerar_pendencias: boolean;
  eh_pe: boolean;
  ciclo_pe_id: number | null;
  amostras_coletadas: number; pendencia: string | null;
  dep_a1: number; dep_a2: number; dep_b: number; dep_c: number;
  dep_d1: number; dep_d2: number; dep_e: number;
  dep_eliminados: number; dep_tratados: number;
  imoveis_positivos: number; dep_positivos: number;
  larvicida_l1_tipo: string | null; larvicida_l1_qtd_gramas: number; larvicida_l1_qtd_depositos: number;
  larvicida_l2_tipo: string | null; larvicida_l2_qtd_gramas: number; larvicida_l2_qtd_depositos: number;
  adulticida_tipo: string | null; adulticida_qtd_ml: number; adulticida_cargas: number | null;
  quarteiroes_trabalhados: string[] | null; quarteiroes_concluidos: string[] | null;
  // 14/10/2026: grades ja lidas pelo servidor (so Ciclo 4/2026 em diante; senao null)
  grade_trabalhados_lida?: string[] | null; grade_concluidos_lida?: string[] | null;
  observacoes: string | null;
  lab_quart_aegypti: string[] | null; lab_quart_albopictus: string[] | null;
  lab_dep_aeg_a1: number|null; lab_dep_aeg_a2: number|null; lab_dep_aeg_b: number|null;
  lab_dep_aeg_c: number|null;  lab_dep_aeg_d1: number|null; lab_dep_aeg_d2: number|null; lab_dep_aeg_e: number|null;
  lab_dep_alb_a1: number|null; lab_dep_alb_a2: number|null; lab_dep_alb_b: number|null;
  lab_dep_alb_c: number|null;  lab_dep_alb_d1: number|null; lab_dep_alb_d2: number|null; lab_dep_alb_e: number|null;
  lab_imov_aeg_r: number|null; lab_imov_aeg_c: number|null; lab_imov_aeg_tb: number|null;
  lab_imov_aeg_pe: number|null; lab_imov_aeg_o: number|null;
  lab_imov_alb_r: number|null; lab_imov_alb_c: number|null; lab_imov_alb_tb: number|null;
  lab_imov_alb_pe: number|null; lab_imov_alb_o: number|null;
  lab_imov_outros_r: number|null; lab_imov_outros_c: number|null; lab_imov_outros_tb: number|null;
  lab_imov_outros_pe: number|null; lab_imov_outros_o: number|null;
  lab_ex_aeg_larvas: number|null; lab_ex_aeg_pupas: number|null; lab_ex_aeg_exuvias: number|null; lab_ex_aeg_adultos: number|null;
  lab_ex_alb_larvas: number|null; lab_ex_alb_pupas: number|null; lab_ex_alb_exuvias: number|null; lab_ex_alb_adultos: number|null;
  lab_ex_outros_larvas: number|null; lab_ex_outros_pupas: number|null; lab_ex_outros_exuvias: number|null; lab_ex_outros_adultos: number|null;
  lab_assinatura: string | null;
  lab_data_processamento: string | null;
  lab_agente_analise: string | null;
}

// ── Endereço ──────────────────────────────────────────────────────────────────
interface EnderecoItem {
  quarteirao: string; seq_lado: string; logradouro: string;
  numero_imovel: string; seq_imovel: string; complemento: string; tipo_imovel: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const ATIVIDADES = ['LI', 'LI+T', 'PE', 'T', 'DF', 'PVE', 'ID', 'BT', 'RA'];
const LARVICIDAS = ['', 'BTI (Bacillus thuringiensis)', 'BsFi (Bacillus sphaericus)',
  'Temephos (Abate)', 'Pyriproxyfen', 'Diflubenzuron', 'Biolarvicida'];
// Sessao 31 (29/09/2026): o tipo nao nasce BTI. So quando o usuario digita gramas ou
// depositos > 0 numa linha sem tipo, a tela sugere BTI (visivel no select, pode trocar).
const comUsoLarvicida = (f: any, tipo: string, campo: string, valor: number) => ({
  ...f, [campo]: valor,
  [tipo]: valor > 0 && !f[tipo] ? 'BTI (Bacillus thuringiensis)' : f[tipo],
});
const ADULTICIDAS = ['', 'Malathion', 'Cypermethrin', 'Deltamethrin', 'Sumithion', 'Bendiocarb'];

const emptyGrid = (n: number): string[] => Array(n).fill('');

// Devolve uma grade de n posicoes a partir de DUAS fontes possiveis:
//   1. o array da grade (FAD-01 digitado a mao) — tem prioridade
//   2. o texto separado por virgula (FAD-01 importado da planilha)
// Se nenhuma tiver conteudo, devolve a grade vazia.
const gradeOuTexto = (
  arr: string[] | null | undefined,
  texto: string | null | undefined,
  n: number,
): string[] => {
  if (arr?.some(x => x)) return [...arr, ...emptyGrid(Math.max(0, n - arr.length))];
  if (texto?.trim()) {
    const codigos = texto.split(',').map(c => c.trim()).filter(c => c).slice(0, n);
    return [...codigos, ...emptyGrid(Math.max(0, n - codigos.length))];
  }
  return emptyGrid(n);
};

// 14/10/2026 (Walber): boletim do Ciclo 4/2026 em diante -> a lista ja vem lida pelo
// leitor unico do servidor ("18,5" = 18.5 quando o RG confirma). Sem lista (Ciclo ate
// o 3/2026, ou resposta antiga do servidor) -> gradeOuTexto, exatamente como sempre.
const gradeDoServidorOu = (
  lida: string[] | null | undefined,
  arr: string[] | null | undefined,
  texto: string | null | undefined,
  n: number,
): string[] => (lida ? [...lida, ...emptyGrid(Math.max(0, n - lida.length))] : gradeOuTexto(arr, texto, n));

// Mesmo corte do servidor (quarteiroes_parser.CICLO_REGRA_NOVA): ano e numero, nunca o id.
const CICLO_REGRA_NOVA = { ano: 2026, numero: 4 };
const ehCicloRegraNova = (c?: Ciclo): boolean =>
  !!c && c.ano != null && c.numero != null &&
  (c.ano > CICLO_REGRA_NOVA.ano || (c.ano === CICLO_REGRA_NOVA.ano && c.numero >= CICLO_REGRA_NOVA.numero));

// No Ciclo 4/2026 em diante cada celula da grade e UM quarteirao: "18,37" vai como "18.37".
const celulasComPonto = (arr: string[] | null | undefined): string[] | null | undefined =>
  arr ? arr.map(c => (c ? String(c).replace(/\s*,\s*/g, '.') : c)) : arr;

const formVazio: any = {
  data: new Date().toISOString().split('T')[0],
  agente_id: '', area_id: '', ciclo_id: '',
  atividade: 'LI', concluida: true,
  quarteirao_texto: '', quarteirao_concluidos_texto: '',
  imoveis_residencia: 0, imoveis_comercio: 0, imoveis_terreno_baldio: 0,
  imoveis_ponto_estrategico: 0, imoveis_outros: 0,
  trat_focal: 0, trat_perifocal: 0, inspecionados: 0,
  recuperados: 0, recusados: 0, fechados: 0,
  gerar_pendencias: true,
  eh_pe: false, ciclo_pe_id: null,
  amostras_coletadas: 0, pendencia: '',
  dep_a1: 0, dep_a2: 0, dep_b: 0, dep_c: 0,
  dep_d1: 0, dep_d2: 0, dep_e: 0,
  dep_eliminados: 0, dep_tratados: 0,
  imoveis_positivos: 0, dep_positivos: 0,
  larvicida_l1_tipo: '', larvicida_l1_qtd_gramas: 0, larvicida_l1_qtd_depositos: 0,
  larvicida_l2_tipo: '', larvicida_l2_qtd_gramas: 0, larvicida_l2_qtd_depositos: 0,
  adulticida_tipo: '', adulticida_qtd_ml: 0, adulticida_cargas: null,
  quarteiroes_trabalhados: emptyGrid(30), quarteiroes_concluidos: emptyGrid(30),
  observacoes: '',
  lab_quart_aegypti: emptyGrid(20), lab_quart_albopictus: emptyGrid(20),
  lab_dep_aeg_a1: null, lab_dep_aeg_a2: null, lab_dep_aeg_b: null,
  lab_dep_aeg_c: null, lab_dep_aeg_d1: null, lab_dep_aeg_d2: null, lab_dep_aeg_e: null,
  lab_dep_alb_a1: null, lab_dep_alb_a2: null, lab_dep_alb_b: null,
  lab_dep_alb_c: null, lab_dep_alb_d1: null, lab_dep_alb_d2: null, lab_dep_alb_e: null,
  lab_imov_aeg_r: null, lab_imov_aeg_c: null, lab_imov_aeg_tb: null,
  lab_imov_aeg_pe: null, lab_imov_aeg_o: null,
  lab_imov_alb_r: null, lab_imov_alb_c: null, lab_imov_alb_tb: null,
  lab_imov_alb_pe: null, lab_imov_alb_o: null,
  lab_imov_outros_r: null, lab_imov_outros_c: null, lab_imov_outros_tb: null,
  lab_imov_outros_pe: null, lab_imov_outros_o: null,
  lab_ex_aeg_larvas: null, lab_ex_aeg_pupas: null, lab_ex_aeg_exuvias: null, lab_ex_aeg_adultos: null,
  lab_ex_alb_larvas: null, lab_ex_alb_pupas: null, lab_ex_alb_exuvias: null, lab_ex_alb_adultos: null,
  lab_ex_outros_larvas: null, lab_ex_outros_pupas: null, lab_ex_outros_exuvias: null, lab_ex_outros_adultos: null,
  lab_assinatura: '',
  lab_data_processamento: '',
  lab_agente_analise: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const corAtividade: Record<string, { bg: string; text: string }> = {
  'LI':   { bg: '#EFF6FF', text: '#1B3A6B' }, 'LI+T': { bg: '#DBEAFE', text: '#1E40AF' },
  'PE':   { bg: '#EDE9FE', text: '#4C1D95' }, 'T':    { bg: '#D1FAE5', text: '#065F46' },
  'DF':   { bg: '#FEF3C7', text: '#92400E' }, 'PVE':  { bg: '#FEE2E2', text: '#991B1B' },
  'ID':   { bg: '#F0FDF4', text: '#0F5132' }, 'BT':   { bg: '#FFF7ED', text: '#C2410C' },
  'RA':   { bg: '#F8FAFF', text: '#374151' },
};
const fmtG = (n: number | null | undefined) => (n && n > 0)
  ? parseFloat(String(n)).toFixed(1).replace('.', ',') + ' g' : '—';
const totalImoveis = (r: Fad01) =>
  r.imoveis_residencia + r.imoveis_comercio + r.imoveis_terreno_baldio +
  r.imoveis_ponto_estrategico + r.imoveis_outros;
const totalDep = (r: Fad01) =>
  r.dep_a1 + r.dep_a2 + r.dep_b + r.dep_c + r.dep_d1 + r.dep_d2 + r.dep_e;
const labPreenchido = (r: Fad01) =>
  !!(r.lab_quart_aegypti?.some(x => x) || r.lab_quart_albopictus?.some(x => x) ||
     r.lab_dep_aeg_a1 || r.lab_dep_alb_a1 || r.lab_ex_aeg_larvas || r.lab_assinatura);

// Algum numero de laboratorio > 0 ou quarteirao positivo listado no formulario
// (mesmo criterio de tem_laboratorio no motor, app/utils/consistencia_lab.py).
const labNoForm = (f: any) =>
  Object.keys(f).some(k => /^lab_(dep|imov|ex)_/.test(k) && Number(f[k]) > 0) ||
  !!(f.lab_quart_aegypti?.some((x: any) => x) || f.lab_quart_albopictus?.some((x: any) => x));

// ── Table style helpers ───────────────────────────────────────────────────────
const TH_STYLE: React.CSSProperties = {
  border: '1px solid #D1D5DB', padding: '5px 8px', background: '#F3F4F6',
  fontSize: '0.7rem', fontWeight: 600, color: '#374151', textAlign: 'center',
  whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif',
};
const TD_STYLE: React.CSSProperties = {
  border: '1px solid #D1D5DB', padding: 0, textAlign: 'center',
};
const INPUT_STYLE: React.CSSProperties = {
  width: '100%', border: 'none', outline: 'none', textAlign: 'center',
  padding: '6px 4px', fontSize: '0.85rem', fontWeight: 600,
  fontVariantNumeric: 'tabular-nums', background: 'transparent',
  fontFamily: 'Inter, sans-serif', minWidth: 44,
};
const TOTAL_STYLE: React.CSSProperties = {
  ...INPUT_STYLE, background: '#EFF6FF', color: '#1B3A6B', fontWeight: 800, cursor: 'default',
};

// ── Sub-components ────────────────────────────────────────────────────────────
function NumCell({ f, form, setForm, color, disabled }: { f: string; form: any; setForm: any; color?: string; disabled?: boolean }) {
  return (
    <td style={TD_STYLE}>
      <input type="number" min={0} value={form[f] || ''} placeholder="0"
        disabled={disabled}
        style={{ ...INPUT_STYLE, color: color || '#1F2937', opacity: disabled ? 0.35 : 1, cursor: disabled ? 'not-allowed' : 'text', backgroundColor: disabled ? '#F3F4F6' : undefined }}
        onChange={e => { if (!disabled) setForm((prev: any) => ({ ...prev, [f]: Number(e.target.value) || 0 })); }}
      />
    </td>
  );
}
function NumCellNull({ f, form, setForm }: { f: string; form: any; setForm: any }) {
  return (
    <td style={TD_STYLE}>
      <input type="number" min={0} value={form[f] ?? ''} placeholder="—"
        style={INPUT_STYLE}
        onChange={e => setForm((prev: any) => ({ ...prev, [f]: e.target.value === '' ? null : Number(e.target.value) }))}
      />
    </td>
  );
}
function TotalCell({ value, color }: { value: number; color?: string }) {
  return (
    <td style={{ ...TD_STYLE }}>
      <div style={{ ...TOTAL_STYLE, color: color || '#1B3A6B' }}>{value || 0}</div>
    </td>
  );
}
function SelectCell({ f, form, setForm, opts }: { f: string; form: any; setForm: any; opts: string[] }) {
  return (
    <td style={{ ...TD_STYLE, minWidth: 180 }}>
      <select value={form[f] || ''} style={{ ...INPUT_STYLE, minWidth: 170, cursor: 'pointer' }}
        onChange={e => setForm((prev: any) => ({ ...prev, [f]: e.target.value || null }))}>
        {opts.map(o => <option key={o} value={o}>{o || '— Nenhum —'}</option>)}
      </select>
    </td>
  );
}
function BlocoTitulo({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1B3A6B', textTransform: 'uppercase',
      letterSpacing: '0.08em', mb: 0.5, mt: 2 }}>
      {children}
    </Typography>
  );
}

// Grade de caixinhas para quarteirões
function GradeQuarteirao({ value, onChange, rows, cols, readOnly }: {
  value: string[]; onChange: (v: string[]) => void; rows: number; cols: number;
  readOnly?: boolean;
}) {
  const total = rows * cols;
  const grid  = Array(total).fill('').map((_, i) => value[i] ?? '');
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {Array(rows).fill(null).map((_, row) => (
            <tr key={row}>
              {Array(cols).fill(null).map((_, col) => {
                const i = row * cols + col;
                return (
                  <td key={col} style={{ border: '1px solid #D1D5DB', padding: 0 }}>
                    <input value={grid[i]} maxLength={6} readOnly={readOnly}
                      onChange={e => {
                        const next = [...grid];
                        next[i] = e.target.value;
                        onChange(next);
                      }}
                      style={{
                        width: 44, height: 28, border: 'none', outline: 'none',
                        textAlign: 'center', fontSize: '0.72rem', fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        background: readOnly
                          ? (grid[i] ? '#F3F4F6' : 'transparent')
                          : (grid[i] ? '#EFF6FF' : 'transparent'),
                        color: readOnly ? '#374151' : 'inherit',
                        fontFamily: 'Inter, sans-serif', cursor: readOnly ? 'default' : 'text',
                      }}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function SecaoHeader({ titulo, cor }: { titulo: string; cor: string }) {
  return (
    <Box sx={{ px: 2, py: 1, background: `${cor}18`, borderBottom: `2px solid ${cor}`, borderRadius: '8px 8px 0 0' }}>
      <Typography sx={{ fontWeight: 800, color: cor, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
        {titulo}
      </Typography>
    </Box>
  );
}

interface Props {
  onVoltar: () => void;
  initialFad01Id?: number;
  onVoltarLaboratorio?: () => void;
}

// ══════════════════════════════════════════════════════════════════════════════
export default function FAD01({ onVoltar, initialFad01Id, onVoltarLaboratorio }: Props) {
  const { token } = useAuth();
  const [vista, setVista] = useState<'lista' | 'form' | 'visualizar'>('lista');
  const [registros, setRegistros]   = useState<Fad01[]>([]);
  const [agentes, setAgentes]       = useState<Agente[]>([]);
  const [areas, setAreas]           = useState<Area[]>([]);
  const [ciclos, setCiclos]         = useState<Ciclo[]>([]);
  const [ciclosPe, setCiclosPe]     = useState<CicloPe[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando]     = useState(false);
  const [erro, setErro]             = useState('');
  const [sucesso, setSucesso]       = useState('');
  const [aviso, setAviso]           = useState('');
  const [form, setForm]             = useState<any>(formVazio);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [visualizando, setVisualizando] = useState<Fad01 | null>(null);
  const [filtroCiclo, setFiltroCiclo]   = useState('');
  const [confirmarDelete, setConfirmarDelete] = useState<number | null>(null);
  const [labAberto, setLabAberto]       = useState(false);
  const [labVizAberto, setLabVizAberto] = useState(false);
  const [filtroAgente, setFiltroAgente] = useState('');
  const [filtroArea, setFiltroArea]     = useState('');
  const [filtroSE, setFiltroSE]         = useState<number | null>(null);
  const [hoje, setHoje]                  = useState<number>(0);

  // Conferencia do laboratorio (Sessao 29) — quem decide e o motor
  const [conferencia, setConferencia]       = useState<ResultadoConferencia | null>(null);
  const [conferenciaFalhou, setConferenciaFalhou] = useState(false);
  const [conferindo, setConferindo]         = useState(false);
  const [avisosSalvar, setAvisosSalvar]     = useState<ResultadoConferencia | null>(null);
  const conferenciaSeq = useRef(0);
  const conferenciaRef = useRef<HTMLDivElement>(null);

  const [, setEnderecosFechados]    = useState<EnderecoItem[]>([]);
  const [, setEnderecosRecusados]   = useState<EnderecoItem[]>([]);
  const [, setEnderecosRecuperados] = useState<EnderecoItem[]>([]);

  const headers = { Authorization: `Bearer ${token}` };

  const recarregarCiclosPe = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/ciclos-pe/`, { headers });
      setCiclosPe(r.data);
      return r.data;
    } catch { return null; }
  }, [token]); // eslint-disable-line

  const [criandoPe, setCriandoPe] = useState(false);

  const criarNovoCicloPe = useCallback(async () => {
    setCriandoPe(true);
    try {
      const { data: novo } = await axios.post(
        `${API_URL}/api/ciclos-pe/`,
        { ano: new Date().getFullYear() },
        { headers }
      );
      await recarregarCiclosPe();
      setForm((f: any) => ({ ...f, ciclo_pe_id: String(novo.id) }));
      return novo;
    } catch (e: any) {
      setErro(e?.response?.data?.detail || 'Erro ao criar novo Ciclo PE.');
      return null;
    } finally { setCriandoPe(false); }
  }, [token, recarregarCiclosPe]); // eslint-disable-line

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [ra, rarea, rci, rfad, rcpe] = await Promise.all([
        axios.get(`${API_URL}/api/agentes/`, { headers }),
        axios.get(`${API_URL}/api/areas/`, { headers }),
        axios.get(`${API_URL}/api/ciclos/`, { headers }),
        axios.get(`${API_URL}/api/fad01/`, { headers }),
        axios.get(`${API_URL}/api/ciclos-pe/`, { headers }),
      ]);
      setAgentes(ra.data.filter((a: Agente) => a.status === 'ativo'));
      setAreas(rarea.data.filter((a: Area) => a.ativa));
      setCiclos(rci.data);
      setRegistros(rfad.data);
      setCiclosPe(rcpe.data);
      const cicloAtivo = rci.data.find((c: Ciclo) => c.ativo);
      if (cicloAtivo) setFiltroCiclo(String(cicloAtivo.id));
    } catch { setErro('Erro ao carregar dados.'); }
    finally  { setCarregando(false); }
  }, [token]); // eslint-disable-line

  const fetchHoje = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/painel-home/dados`);
      setHoje(r.data?.lancamentos_hoje || 0);
    } catch {
      // silencioso — chip apenas informativo
    }
  }, []); // eslint-disable-line

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { fetchHoje(); }, [fetchHoje]);

  useEffect(() => {
    if (!initialFad01Id) return;
    if (registros.length === 0) return;
    const alvo = registros.find(r => r.id === initialFad01Id);
    if (alvo) { abrirEditar(alvo); return; }
    // Bloco F1: boletim fora da lista carregada -> busca direto pelo id.
    axios.get<Fad01>(`${API_URL}/api/fad01/${initialFad01Id}`, { headers })
      .then(r => abrirEditar(r.data))
      .catch(() => setErro(`Não foi possível abrir o boletim nº ${initialFad01Id}.`));
  }, [initialFad01Id, registros]); // eslint-disable-line

  const ajustarArray = (atual: any[], n: number) => {
    if (n > atual.length) return [...atual, ...Array(n - atual.length).fill(null).map(() => ({ quarteirao: '', seq_lado: '', logradouro: '', numero_imovel: '', seq_imovel: '', complemento: '', tipo_imovel: 'R' }))];
    return atual.slice(0, n);
  };

  useEffect(() => {
    setEnderecosFechados(prev => ajustarArray(prev, Math.max(0, form.fechados ?? 0)));
  }, [form.fechados]);

  useEffect(() => {
    setEnderecosRecusados(prev => ajustarArray(prev, Math.max(0, form.recusados ?? 0)));
  }, [form.recusados]);

  useEffect(() => {
    setEnderecosRecuperados(prev => ajustarArray(prev, Math.max(0, form.recuperados ?? 0)));
  }, [form.recuperados]);

  const abrirVisualizar = (r: Fad01) => {
    setVisualizando(r);
    setLabVizAberto(labPreenchido(r));
    setVista('visualizar');
  };

  const abrirNovo = () => {
    const cicloAtivo = ciclos.find(c => c.ativo);
    setForm({ ...formVazio, ciclo_id: cicloAtivo ? String(cicloAtivo.id) : '' });
    setEditandoId(null); setLabAberto(false);
    setEnderecosFechados([]); setEnderecosRecusados([]); setEnderecosRecuperados([]);
    setVista('form');
  };

  const abrirEditar = async (r: Fad01) => {
    setEnderecosFechados([]); setEnderecosRecusados([]); setEnderecosRecuperados([]);
    try {
      const { data: pends } = await axios.get(`${API_URL}/api/pendencias/`, { headers, params: { fad01_origem_id: r.id } });
      const toItem = (p: any) => ({ quarteirao: p.quarteirao || '', seq_lado: p.seq_lado || '', logradouro: p.logradouro || '', numero_imovel: p.numero_imovel || '', seq_imovel: p.seq_imovel || '', complemento: p.complemento || '', tipo_imovel: p.tipo_imovel || 'R' });
      setEnderecosFechados(pends.filter((p: any) => p.tipo_pendencia === 'F').map(toItem));
      setEnderecosRecusados(pends.filter((p: any) => p.tipo_pendencia === 'R').map(toItem));
    } catch { /* silencioso — não bloqueia abertura do form */ }
    // Sessao 29 (23/09/2026 - Walber): parte do formulario vazio e copia do boletim
    // TODO campo que o formulario tem. Antes, "Outros" do laboratorio, as exuvias e
    // gerar_pendencias ficavam de fora e iam para o motor como 0 / true ao salvar.
    // As conversoes especiais abaixo continuam valendo (vem depois e sobrepoem).
    const doBoletim: any = {};
    Object.keys(formVazio).forEach(k => { if (k in r) doBoletim[k] = (r as any)[k]; });
    setForm({
      ...formVazio,
      ...doBoletim,
      data: r.data, agente_id: String(r.agente.id), area_id: String(r.area.id),
      ciclo_id: String(r.ciclo.id), atividade: r.atividade, concluida: r.concluida ?? true,
      quarteirao_texto: r.quarteirao_texto || '',
      quarteirao_concluidos_texto: r.quarteirao_concluidos_texto || '',
      imoveis_residencia: r.imoveis_residencia, imoveis_comercio: r.imoveis_comercio,
      imoveis_terreno_baldio: r.imoveis_terreno_baldio,
      imoveis_ponto_estrategico: r.imoveis_ponto_estrategico, imoveis_outros: r.imoveis_outros,
      trat_focal: r.trat_focal, trat_perifocal: r.trat_perifocal, inspecionados: r.inspecionados,
      recuperados: r.recuperados, recusados: r.recusados, fechados: r.fechados,
      amostras_coletadas: r.amostras_coletadas, pendencia: r.pendencia || '',
      dep_a1: r.dep_a1, dep_a2: r.dep_a2, dep_b: r.dep_b, dep_c: r.dep_c,
      dep_d1: r.dep_d1, dep_d2: r.dep_d2, dep_e: r.dep_e,
      dep_eliminados: r.dep_eliminados, dep_tratados: r.dep_tratados,
      imoveis_positivos: r.imoveis_positivos, dep_positivos: r.dep_positivos,
      larvicida_l1_tipo: r.larvicida_l1_tipo || '',
      larvicida_l1_qtd_gramas: r.larvicida_l1_qtd_gramas,
      larvicida_l1_qtd_depositos: r.larvicida_l1_qtd_depositos,
      larvicida_l2_tipo: r.larvicida_l2_tipo || '',
      larvicida_l2_qtd_gramas: r.larvicida_l2_qtd_gramas,
      larvicida_l2_qtd_depositos: r.larvicida_l2_qtd_depositos,
      adulticida_tipo: r.adulticida_tipo || '', adulticida_qtd_ml: r.adulticida_qtd_ml,
      adulticida_cargas: r.adulticida_cargas,
      quarteiroes_trabalhados: gradeDoServidorOu(r.grade_trabalhados_lida, r.quarteiroes_trabalhados, r.quarteirao_texto, 30),
      quarteiroes_concluidos: r.quarteiroes_concluidos?.length
        ? [...r.quarteiroes_concluidos, ...emptyGrid(Math.max(0, 30 - r.quarteiroes_concluidos.length))]
        : emptyGrid(30),
      observacoes: r.observacoes || '',
      lab_quart_aegypti: r.lab_quart_aegypti?.length
        ? [...r.lab_quart_aegypti, ...emptyGrid(Math.max(0, 20 - r.lab_quart_aegypti.length))]
        : emptyGrid(20),
      lab_quart_albopictus: r.lab_quart_albopictus?.length
        ? [...r.lab_quart_albopictus, ...emptyGrid(Math.max(0, 20 - r.lab_quart_albopictus.length))]
        : emptyGrid(20),
      lab_dep_aeg_a1: r.lab_dep_aeg_a1, lab_dep_aeg_a2: r.lab_dep_aeg_a2,
      lab_dep_aeg_b: r.lab_dep_aeg_b, lab_dep_aeg_c: r.lab_dep_aeg_c,
      lab_dep_aeg_d1: r.lab_dep_aeg_d1, lab_dep_aeg_d2: r.lab_dep_aeg_d2, lab_dep_aeg_e: r.lab_dep_aeg_e,
      lab_dep_alb_a1: r.lab_dep_alb_a1, lab_dep_alb_a2: r.lab_dep_alb_a2,
      lab_dep_alb_b: r.lab_dep_alb_b, lab_dep_alb_c: r.lab_dep_alb_c,
      lab_dep_alb_d1: r.lab_dep_alb_d1, lab_dep_alb_d2: r.lab_dep_alb_d2, lab_dep_alb_e: r.lab_dep_alb_e,
      lab_imov_aeg_r: r.lab_imov_aeg_r, lab_imov_aeg_c: r.lab_imov_aeg_c,
      lab_imov_aeg_tb: r.lab_imov_aeg_tb, lab_imov_aeg_pe: r.lab_imov_aeg_pe, lab_imov_aeg_o: r.lab_imov_aeg_o,
      lab_imov_alb_r: r.lab_imov_alb_r, lab_imov_alb_c: r.lab_imov_alb_c,
      lab_imov_alb_tb: r.lab_imov_alb_tb, lab_imov_alb_pe: r.lab_imov_alb_pe, lab_imov_alb_o: r.lab_imov_alb_o,
      lab_ex_aeg_larvas: r.lab_ex_aeg_larvas, lab_ex_aeg_pupas: r.lab_ex_aeg_pupas,
      lab_ex_aeg_adultos: r.lab_ex_aeg_adultos,
      lab_ex_alb_larvas: r.lab_ex_alb_larvas, lab_ex_alb_pupas: r.lab_ex_alb_pupas,
      lab_ex_alb_adultos: r.lab_ex_alb_adultos,
      lab_assinatura: r.lab_assinatura || '',
      lab_data_processamento: r.lab_data_processamento || '',
      lab_agente_analise: r.lab_agente_analise || '',
      eh_pe: r.eh_pe ?? false,
      ciclo_pe_id: r.ciclo_pe_id ?? null,
    });
    setEditandoId(r.id);
    setLabAberto(labPreenchido(r));
    setVista('form');
  };

  // Corpo enviado ao motor — o MESMO para salvar e para conferir o laboratorio.
  const montarPayload = (f: any) => {
      // 14/10/2026: Ciclo 4/2026 em diante -> virgula da celula vira ponto. Ate o 3/2026: igual.
      const novo = ehCicloRegraNova(ciclos.find(c => c.id === Number(f.ciclo_id)));
      const cel = (a: any) => (novo ? celulasComPonto(a) : a);
      return {
        ...f,
        agente_id: Number(f.agente_id), area_id: Number(f.area_id),
        ciclo_id: Number(f.ciclo_id),
        quarteirao_texto: f.quarteirao_texto || null,
        quarteirao_concluidos_texto: f.quarteirao_concluidos_texto || null,
        // Sessao 31 (29/09/2026): tipo so vai quando a linha teve uso (gramas ou depositos)
        larvicida_l1_tipo: (Number(f.larvicida_l1_qtd_gramas) > 0 || Number(f.larvicida_l1_qtd_depositos) > 0) ? (f.larvicida_l1_tipo || null) : null,
        larvicida_l2_tipo: (Number(f.larvicida_l2_qtd_gramas) > 0 || Number(f.larvicida_l2_qtd_depositos) > 0) ? (f.larvicida_l2_tipo || null) : null,
        adulticida_tipo: f.adulticida_tipo || null,
        pendencia: f.pendencia || null,
        observacoes: f.observacoes || null,
        lab_assinatura: f.lab_assinatura || null,
        lab_data_processamento: f.lab_data_processamento || null,
        lab_agente_analise: f.lab_agente_analise || null,
        // Trimmar arrays — remover trailing vazios mas manter os que têm conteúdo
        quarteiroes_trabalhados: cel(f.quarteiroes_trabalhados),
        quarteiroes_concluidos: cel(f.quarteiroes_concluidos),
        lab_quart_aegypti: cel(f.lab_quart_aegypti),
        lab_quart_albopictus: cel(f.lab_quart_albopictus),
        eh_pe: f.eh_pe || false,
        ciclo_pe_id: f.eh_pe ? Number(f.ciclo_pe_id) : null,
      };
  };

  // Pergunta ao motor (so leitura). Devolve null se a conferencia falhar:
  // nesse caso a tela nao trava e o motor decide no salvamento.
  const conferirLaboratorio = async (f: any, id: number | null): Promise<ResultadoConferencia | null> => {
    const seq = ++conferenciaSeq.current;
    setConferindo(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/fad01/validar-laboratorio`,
        { ...montarPayload(f), id }, { headers });
      const r: ResultadoConferencia = { travas: data?.travas ?? [], avisos: data?.avisos ?? [] };
      if (seq === conferenciaSeq.current) { setConferencia(r); setConferenciaFalhou(false); }
      return r;
    } catch {
      if (seq === conferenciaSeq.current) { setConferencia(null); setConferenciaFalhou(true); }
      return null;
    } finally {
      if (seq === conferenciaSeq.current) setConferindo(false);
    }
  };

  // Confere ~600 ms depois da ultima digitacao; respostas antigas sao ignoradas (seq)
  useEffect(() => {
    if (vista !== 'form' || form.eh_pe || (!labNoForm(form) && !editandoId)) {
      conferenciaSeq.current++;
      setConferencia(null); setConferenciaFalhou(false); setConferindo(false);
      return;
    }
    const t = setTimeout(() => { conferirLaboratorio(form, editandoId); }, 600);
    return () => clearTimeout(t);
  }, [form, editandoId, vista]); // eslint-disable-line

  const salvar = async (ignorarAvisos: boolean = false) => {
    if (!form.data || !form.agente_id || !form.area_id || !form.ciclo_id) {
      setErro('Preencha data, agente, área e ciclo.'); return;
    }
    if (form.eh_pe && !form.ciclo_pe_id) {
      setErro('Para lançamento PE, selecione o ciclo PE.'); return;
    }
    setSalvando(true);
    if (!form.eh_pe) {
      const conf = await conferirLaboratorio(form, editandoId);
      if (conf && conf.travas.length > 0) {
        setErro('Inconsistência no resultado de laboratório:\n- ' + conf.travas.map(textoItens).join('\n- '));
        setLabAberto(true);
        setTimeout(() => conferenciaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        setSalvando(false);
        return;
      }
      if (conf && conf.avisos.length > 0 && !ignorarAvisos) {
        setAvisosSalvar(conf);
        setSalvando(false);
        return;
      }
    }
    try {
      const payload = montarPayload(form);
      const respFad = editandoId
        ? await axios.put(`${API_URL}/api/fad01/${editandoId}`, payload, { headers })
        : await axios.post(`${API_URL}/api/fad01/`, payload, { headers });
      const fad01Id: number = respFad.data.id;
      window.dispatchEvent(new CustomEvent('tovis:fad01-salvo', { detail: { id: fad01Id } }));
      const se = getSE(form.data);
      const baseEnd = { data_registro: form.data, se, ciclo_id: Number(form.ciclo_id), agente_id: Number(form.agente_id), area_id: Number(form.area_id), fad01_origem_id: fad01Id };
      setSucesso(editandoId ? 'FAD-01 atualizada!' : 'FAD-01 salva com sucesso!');
      await carregar();
      await fetchHoje();
      setVista('lista');
      if (initialFad01Id) {
        if (onVoltarLaboratorio) {
          onVoltarLaboratorio();
        } else {
          onVoltar();
        }
        return;
      }
      setTimeout(() => setSucesso(''), 4000);
      if (!editandoId && form.gerar_pendencias) {
        try {
          // Criações F e R em paralelo (sem risco de race entre si)
          const promFechados = Array.from({ length: form.fechados || 0 }, () =>
            axios.post(`${API_URL}/api/pendencias/`, { ...baseEnd, tipo_pendencia: 'F', status: 'PENDENTE' }, { headers })
          );
          const promRecusados = Array.from({ length: form.recusados || 0 }, () =>
            axios.post(`${API_URL}/api/pendencias/`, { ...baseEnd, tipo_pendencia: 'R', status: 'PENDENTE' }, { headers })
          );
          await Promise.all([...promFechados, ...promRecusados]);

          // Recuperações SEMPRE sequenciais: cada await espera o commit anterior
          // antes de tentar — necessário pro lock SKIP LOCKED do backend funcionar
          const qtdRec = form.recuperados || 0;
          let casou = 0;
          let naoEncontradas = 0;
          for (let i = 0; i < qtdRec; i++) {
            try {
              const r = await axios.post(`${API_URL}/api/pendencias/tentar-recuperar`, {
                agente_id: Number(form.agente_id),
                area_id: Number(form.area_id),
                fad01_recuperacao_id: fad01Id,
              }, { headers });
              if (r.data?.encontrada) casou++;
              else naoEncontradas++;
            } catch (err) {
              console.error(`Recuperação ${i + 1}/${qtdRec} falhou:`, err);
              naoEncontradas++;
            }
          }

          if (naoEncontradas > 0) {
            setAviso(`Atenção: declarou ${qtdRec} recuperado(s), mas só ${casou} casou(aram) com pendências reais. ${naoEncontradas} sem match — talvez não existam pendências PENDENTE elegíveis para esse agente/área.`);
          }
        } catch {
          setAviso('FAD-01 salvo, mas houve falha ao registrar pendências. Edite o registro depois.');
        }
      }
    } catch (e: any) {
      if (e.response?.status === 400) {
        setErro(e.response.data.detail);
      } else if (!e.response) {
        setErro('Erro de conexão. Verifique sua internet ou tente novamente.');
      } else {
        setErro(e.response?.data?.detail || 'Erro ao salvar. Tente novamente.');
      }
    }
    finally { setSalvando(false); }
  };

  const deletar = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/api/fad01/${id}`, { headers });
      setRegistros(prev => prev.filter(r => r.id !== id));
      setSucesso('Registro removido.'); setTimeout(() => setSucesso(''), 3000);
    } catch { setErro('Erro ao remover.'); }
    finally { setConfirmarDelete(null); }
  };

  const getSE = (dataStr: string): number => dataParaSE(new Date(dataStr + 'T12:00:00'));

  const registrosFiltrados = registros
    .filter(r => !filtroCiclo  || String(r.ciclo.id) === filtroCiclo)
    .filter(r => !filtroAgente || r.agente.nome === filtroAgente)
    .filter(r => !filtroArea   || r.area.nome   === filtroArea)
    .filter(r => filtroSE === null || getSE(r.data) === filtroSE);

  // ── Totais reativos ────────────────────────────────────────────────────────
  const totImov = form.imoveis_residencia + form.imoveis_comercio +
    form.imoveis_terreno_baldio + form.imoveis_ponto_estrategico + form.imoveis_outros;
  const totDep  = form.dep_a1 + form.dep_a2 + form.dep_b + form.dep_c +
    form.dep_d1 + form.dep_d2 + form.dep_e;
  const totLabDepAeg = (form.lab_dep_aeg_a1||0)+(form.lab_dep_aeg_a2||0)+(form.lab_dep_aeg_b||0)+
    (form.lab_dep_aeg_c||0)+(form.lab_dep_aeg_d1||0)+(form.lab_dep_aeg_d2||0)+(form.lab_dep_aeg_e||0);
  const totLabDepAlb = (form.lab_dep_alb_a1||0)+(form.lab_dep_alb_a2||0)+(form.lab_dep_alb_b||0)+
    (form.lab_dep_alb_c||0)+(form.lab_dep_alb_d1||0)+(form.lab_dep_alb_d2||0)+(form.lab_dep_alb_e||0);
  const totLabImovAeg = (form.lab_imov_aeg_r||0)+(form.lab_imov_aeg_c||0)+(form.lab_imov_aeg_tb||0)+
    (form.lab_imov_aeg_pe||0)+(form.lab_imov_aeg_o||0);
  const totLabImovAlb = (form.lab_imov_alb_r||0)+(form.lab_imov_alb_c||0)+(form.lab_imov_alb_tb||0)+
    (form.lab_imov_alb_pe||0)+(form.lab_imov_alb_o||0);
  const totExAeg = (form.lab_ex_aeg_larvas||0)+(form.lab_ex_aeg_pupas||0)+(form.lab_ex_aeg_exuvias||0)+(form.lab_ex_aeg_adultos||0);
  const totExAlb = (form.lab_ex_alb_larvas||0)+(form.lab_ex_alb_pupas||0)+(form.lab_ex_alb_exuvias||0)+(form.lab_ex_alb_adultos||0);
  const totExOutros = (form.lab_ex_outros_larvas||0)+(form.lab_ex_outros_pupas||0)+(form.lab_ex_outros_exuvias||0)+(form.lab_ex_outros_adultos||0);

  if (carregando) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #1B3A6B, #2D5A9E)' }}>
      <CircularProgress sx={{ color: 'white' }} size={50} />
    </Box>
  );

  // ════════════════════════════════════════════════════════
  // VISUALIZAR
  // ════════════════════════════════════════════════════════
  if (vista === 'visualizar' && visualizando) {
    const r = visualizando;
    const corAtiv = corAtividade[r.atividade] || { bg: '#F3F4F6', text: '#374151' };
    const temLab  = labPreenchido(r);
    const rowStyle: React.CSSProperties = { background: 'white' };
    const thV: React.CSSProperties = { ...TH_STYLE, padding: '4px 10px' };
    const tdV: React.CSSProperties = {
      border: '1px solid #E5E7EB', padding: '5px 10px', textAlign: 'center',
      fontSize: '0.82rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
    };
    const fmtV = (n: number | null | undefined) => (n && n > 0) ? String(n) : '—';
    const panelSx = { mb: 2, p: 1.5, border: '1px solid #E5E7EB', borderRadius: 1.5 };

    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F4FF', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" className="no-print" sx={{ background: 'linear-gradient(135deg, #0F2347, #1B3A6B)' }}>
          <Toolbar>
            <IconButton color="inherit" onClick={() => setVista('lista')} sx={{ mr: 1 }}><ArrowBack /></IconButton>
            <Assignment sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>Visualizar FAD-01</Typography>
            <Button variant="contained" startIcon={<Print />} onClick={() => window.print()}
              sx={{ bgcolor: '#065F46', '&:hover': { bgcolor: '#047857' }, fontWeight: 700, borderRadius: 2 }}>
              Imprimir
            </Button>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto', width: '100%' }}>
          {/* Identificação */}
          <Card sx={{ borderRadius: 2, border: '1px solid #BFDBFE', mb: 2 }}>
            <SecaoHeader titulo="Identificação" cor="#1B3A6B" />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2 }}>
                {[
                  ['Data', new Date(r.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })],
                  ['Agente', r.agente.nome], ['Área', `${r.area.nome} (${r.area.zona})`],
                  ['Ciclo', r.ciclo.nome],
                ].map(([l, v]) => (
                  <Box key={l}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.2 }}>{l}</Typography>
                    <Typography sx={{ fontWeight: 700, color: '#1F2937', fontSize: '0.88rem' }}>{v}</Typography>
                  </Box>
                ))}
                <Box>
                  <Typography sx={{ fontSize: '0.65rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>Atividade</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip label={r.atividade} size="small" sx={{ bgcolor: corAtiv.bg, color: corAtiv.text, fontWeight: 700 }} />
                    {r.concluida
                      ? <Chip label="Concluída" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700 }} />
                      : <Chip label="Pendente"  size="small" sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700 }} />}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Seção 1 */}
          <Card sx={{ borderRadius: 2, border: '1px solid #BFDBFE', mb: 2 }}>
            <SecaoHeader titulo="Seção 1 — Resumo do Trabalho de Campo" cor="#1B3A6B" />
            <CardContent sx={{ p: 2 }}>
              {/* A */}
              <Box sx={panelSx}>
                <BlocoTitulo>A — Imóveis trabalhados por tipo</BlocoTitulo>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['Residência','Comércio','T. Baldio','P. Estratégico','Outra','TOTAL'].map(h => (
                        <th key={h} style={thV}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody><tr style={rowStyle}>
                      {[r.imoveis_residencia, r.imoveis_comercio, r.imoveis_terreno_baldio,
                        r.imoveis_ponto_estrategico, r.imoveis_outros].map((v, i) => (
                        <td key={i} style={tdV}>{fmtV(v)}</td>
                      ))}
                      <td style={{ ...tdV, background: '#EFF6FF', color: '#1B3A6B', fontWeight: 800 }}>{totalImoveis(r)}</td>
                    </tr></tbody>
                  </table>
                </Box>
              </Box>
              {/* B */}
              <Box sx={panelSx}>
                <BlocoTitulo>B — Situação dos imóveis</BlocoTitulo>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['Trat. Focal','Trat. Perifocal','Inspecionados','Recuperados','Recusa','Fechados'].map(h => (
                        <th key={h} style={thV}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody><tr style={rowStyle}>
                      {[r.trat_focal, r.trat_perifocal, r.inspecionados,
                        r.recuperados, r.recusados, r.fechados].map((v, i) => (
                        <td key={i} style={tdV}>{fmtV(v)}</td>
                      ))}
                    </tr></tbody>
                  </table>
                </Box>
              </Box>
              {/* C / D */}
              <Box sx={{ ...panelSx, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <BlocoTitulo>C — Amostras/Tubitos</BlocoTitulo>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#D1FAE5', border: '1px solid #A7F3D0', borderRadius: 1.5, px: 2, py: 1, width: 'fit-content' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#065F46', fontVariantNumeric: 'tabular-nums' }}>{fmtV(r.amostras_coletadas)}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#065F46', maxWidth: 110, lineHeight: 1.25 }}>amostras coletadas</Typography>
                  </Box>
                </Box>
                {r.pendencia && <Box>
                  <BlocoTitulo>D — Pendência</BlocoTitulo>
                  <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>{r.pendencia}</Typography>
                </Box>}
              </Box>
              {/* E */}
              <Box sx={panelSx}>
                <BlocoTitulo>E — Depósitos inspecionados por tipo</BlocoTitulo>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['A1','A2','B','C','D1','D2','E','TOTAL'].map(h => <th key={h} style={thV}>{h}</th>)}
                    </tr></thead>
                    <tbody><tr style={rowStyle}>
                      {[r.dep_a1,r.dep_a2,r.dep_b,r.dep_c,r.dep_d1,r.dep_d2,r.dep_e].map((v, i) => (
                        <td key={i} style={tdV}>{fmtV(v)}</td>
                      ))}
                      <td style={{ ...tdV, background: '#FEF3C7', color: '#92400E', fontWeight: 800 }}>{totalDep(r)}</td>
                    </tr></tbody>
                  </table>
                </Box>
              </Box>
              {/* F */}
              <Box sx={panelSx}>
                <BlocoTitulo>F — Resultado do tratamento</BlocoTitulo>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr>
                      {['Eliminados','Tratados'].map(h => <th key={h} style={thV}>{h}</th>)}
                    </tr></thead>
                    <tbody><tr style={rowStyle}>
                      <td style={tdV}>{fmtV(r.dep_eliminados)}</td>
                      <td style={tdV}>{fmtV(r.dep_tratados)}</td>
                    </tr></tbody>
                  </table>
                </Box>
              </Box>
              {/* G H side by side */}
              <Box sx={panelSx}>
                <BlocoTitulo>G/H — Larvicida</BlocoTitulo>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {[
                    ['L1', r.larvicida_l1_tipo, r.larvicida_l1_qtd_gramas, r.larvicida_l1_qtd_depositos],
                    ['L2', r.larvicida_l2_tipo, r.larvicida_l2_qtd_gramas, r.larvicida_l2_qtd_depositos],
                  ].map(([lbl, tipo, g, dep]) => (Number(g) > 0 || Number(dep) > 0 || tipo) && (
                    <Box key={String(lbl)} sx={{ p: 1.5, border: '1px solid #D1D5DB', borderRadius: 1, minWidth: 200, background: '#FFFFFF' }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#4C1D95', mb: 0.5 }}>{lbl}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: '#374151' }}><strong>{tipo || '—'}</strong></Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>{fmtG(Number(g))} · {dep} depósitos</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {/* I */}
              {r.adulticida_tipo && (
                <Box sx={panelSx}>
                  <BlocoTitulo>I — Adulticida</BlocoTitulo>
                  <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>
                    {r.adulticida_tipo} · {r.adulticida_cargas != null ? `${r.adulticida_cargas} cargas` : `${r.adulticida_qtd_ml} ml`}
                  </Typography>
                </Box>
              )}
              {/* J / K — Quarteirões trabalhados e concluídos (grade somente leitura) */}
              {(r.quarteiroes_trabalhados?.some(x => x) || r.quarteirao_texto?.trim() ||
                r.quarteiroes_concluidos?.some(x => x) || r.quarteirao_concluidos_texto?.trim()) && (
                <Box sx={{ ...panelSx, mb: 0 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <BlocoTitulo>J — Quarteirões Trabalhados</BlocoTitulo>
                      <GradeQuarteirao
                        value={gradeDoServidorOu(r.grade_trabalhados_lida, r.quarteiroes_trabalhados, r.quarteirao_texto, 30)}
                        onChange={() => {}} rows={3} cols={10} readOnly
                      />
                    </Box>
                    <Box>
                      <BlocoTitulo>K — Quarteirões Concluídos</BlocoTitulo>
                      <GradeQuarteirao
                        value={gradeDoServidorOu(r.grade_concluidos_lida, r.quarteiroes_concluidos, r.quarteirao_concluidos_texto, 30)}
                        onChange={() => {}} rows={3} cols={10} readOnly
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Seção 2 */}
          <Card sx={{ borderRadius: 2, border: `1px solid ${temLab ? '#A7F3D0' : '#E5E7EB'}`, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
              px: 2, py: 1.2, background: temLab ? '#D1FAE520' : '#F9FAFB',
              borderRadius: labVizAberto ? '8px 8px 0 0' : 2 }}
              onClick={() => setLabVizAberto(v => !v)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Science sx={{ fontSize: 18, color: temLab ? '#065F46' : '#9CA3AF' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase',
                  letterSpacing: '0.1em', color: temLab ? '#065F46' : '#6B7280' }}>
                  Seção 2 — Resumo do Laboratório
                </Typography>
                <Chip label={temLab ? '✓ Preenchido' : '○ Pendente'} size="small"
                  sx={{ bgcolor: temLab ? '#D1FAE5' : '#F3F4F6', color: temLab ? '#065F46' : '#6B7280', fontWeight: 600 }} />
              </Box>
              {labVizAberto ? <ExpandLess sx={{ color: '#6B7280' }} /> : <ExpandMore sx={{ color: '#6B7280' }} />}
            </Box>
            <Collapse in={labVizAberto}>
              <CardContent sx={{ p: 2 }}>
                {/* L / M */}
                <Box sx={{ ...panelSx, display: r.lab_quart_aegypti?.some(x=>x) || r.lab_quart_albopictus?.some(x=>x) ? 'block' : 'none' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {r.lab_quart_aegypti?.some(x => x) && (
                      <Box>
                        <BlocoTitulo>L — Quarteirões com Ae. aegypti</BlocoTitulo>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                          {r.lab_quart_aegypti.filter(x => x).map((x, i) => (
                            <Chip key={i} label={x} size="small" sx={{ fontSize: '0.68rem', bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 700 }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {r.lab_quart_albopictus?.some(x => x) && (
                      <Box>
                        <BlocoTitulo>M — Quarteirões com Ae. albopictus</BlocoTitulo>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                          {r.lab_quart_albopictus.filter(x => x).map((x, i) => (
                            <Chip key={i} label={x} size="small" sx={{ fontSize: '0.68rem', bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700 }} />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
                {/* N */}
                <Box sx={panelSx}>
                  <BlocoTitulo>N — Depósitos com espécimes</BlocoTitulo>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={{ ...thV, textAlign: 'left', minWidth: 130 }}>Espécie</th>
                        {['A1','A2','B','C','D1','D2','E','TOTAL'].map(h => <th key={h} style={thV}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        <tr style={rowStyle}>
                          <td style={{ ...tdV, textAlign: 'left', fontStyle: 'italic', color: '#991B1B' }}>Ae. aegypti</td>
                          {[r.lab_dep_aeg_a1,r.lab_dep_aeg_a2,r.lab_dep_aeg_b,r.lab_dep_aeg_c,
                            r.lab_dep_aeg_d1,r.lab_dep_aeg_d2,r.lab_dep_aeg_e].map((v,i) => (
                            <td key={i} style={tdV}>{fmtV(v)}</td>
                          ))}
                          <td style={{ ...tdV, background: '#FEE2E220', fontWeight: 800, color: '#991B1B' }}>
                            {(r.lab_dep_aeg_a1||0)+(r.lab_dep_aeg_a2||0)+(r.lab_dep_aeg_b||0)+
                             (r.lab_dep_aeg_c||0)+(r.lab_dep_aeg_d1||0)+(r.lab_dep_aeg_d2||0)+(r.lab_dep_aeg_e||0) || '—'}
                          </td>
                        </tr>
                        <tr style={rowStyle}>
                          <td style={{ ...tdV, textAlign: 'left', fontStyle: 'italic', color: '#92400E' }}>Ae. albopictus</td>
                          {[r.lab_dep_alb_a1,r.lab_dep_alb_a2,r.lab_dep_alb_b,r.lab_dep_alb_c,
                            r.lab_dep_alb_d1,r.lab_dep_alb_d2,r.lab_dep_alb_e].map((v,i) => (
                            <td key={i} style={tdV}>{fmtV(v)}</td>
                          ))}
                          <td style={{ ...tdV, background: '#FEF3C720', fontWeight: 800, color: '#92400E' }}>
                            {(r.lab_dep_alb_a1||0)+(r.lab_dep_alb_a2||0)+(r.lab_dep_alb_b||0)+
                             (r.lab_dep_alb_c||0)+(r.lab_dep_alb_d1||0)+(r.lab_dep_alb_d2||0)+(r.lab_dep_alb_e||0) || '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>
                {/* O */}
                <Box sx={panelSx}>
                  <BlocoTitulo>O — Imóveis com espécimes</BlocoTitulo>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={{ ...thV, textAlign: 'left', minWidth: 130 }}>Espécie</th>
                        {['R','C','TB','PE','O','TOTAL'].map(h => <th key={h} style={thV}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        <tr style={rowStyle}>
                          <td style={{ ...tdV, textAlign: 'left', fontStyle: 'italic', color: '#991B1B' }}>Ae. aegypti</td>
                          {[r.lab_imov_aeg_r,r.lab_imov_aeg_c,r.lab_imov_aeg_tb,r.lab_imov_aeg_pe,r.lab_imov_aeg_o].map((v,i) => (
                            <td key={i} style={tdV}>{fmtV(v)}</td>
                          ))}
                          <td style={{ ...tdV, fontWeight: 800, color: '#991B1B' }}>
                            {(r.lab_imov_aeg_r||0)+(r.lab_imov_aeg_c||0)+(r.lab_imov_aeg_tb||0)+(r.lab_imov_aeg_pe||0)+(r.lab_imov_aeg_o||0) || '—'}
                          </td>
                        </tr>
                        <tr style={rowStyle}>
                          <td style={{ ...tdV, textAlign: 'left', fontStyle: 'italic', color: '#92400E' }}>Ae. albopictus</td>
                          {[r.lab_imov_alb_r,r.lab_imov_alb_c,r.lab_imov_alb_tb,r.lab_imov_alb_pe,r.lab_imov_alb_o].map((v,i) => (
                            <td key={i} style={tdV}>{fmtV(v)}</td>
                          ))}
                          <td style={{ ...tdV, fontWeight: 800, color: '#92400E' }}>
                            {(r.lab_imov_alb_r||0)+(r.lab_imov_alb_c||0)+(r.lab_imov_alb_tb||0)+(r.lab_imov_alb_pe||0)+(r.lab_imov_alb_o||0) || '—'}
                          </td>
                        </tr>
                        <tr style={rowStyle}>
                          <td style={{ ...tdV, textAlign: 'left', fontStyle: 'italic', color: '#374151' }}>Outros</td>
                          {[r.lab_imov_outros_r,r.lab_imov_outros_c,r.lab_imov_outros_tb,r.lab_imov_outros_pe,r.lab_imov_outros_o].map((v,i) => (
                            <td key={i} style={tdV}>{fmtV(v)}</td>
                          ))}
                          <td style={{ ...tdV, fontWeight: 800, color: '#374151' }}>
                            {(r.lab_imov_outros_r||0)+(r.lab_imov_outros_c||0)+(r.lab_imov_outros_tb||0)+(r.lab_imov_outros_pe||0)+(r.lab_imov_outros_o||0) || '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>
                {/* P */}
                <Box sx={panelSx}>
                  <BlocoTitulo>P — Nº de exemplares</BlocoTitulo>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={{ ...thV, textAlign: 'left', minWidth: 130 }}>Espécie</th>
                        {['Larvas','Pupas','Exúvias','Adultos','TOTAL'].map(h => <th key={h} style={thV}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        <tr style={rowStyle}>
                          <td style={{ ...tdV, textAlign: 'left', fontStyle: 'italic', color: '#991B1B' }}>Ae. aegypti</td>
                          {[r.lab_ex_aeg_larvas,r.lab_ex_aeg_pupas,r.lab_ex_aeg_exuvias,r.lab_ex_aeg_adultos].map((v,i) => (
                            <td key={i} style={tdV}>{fmtV(v)}</td>
                          ))}
                          <td style={{ ...tdV, fontWeight: 800, color: '#991B1B' }}>
                            {(r.lab_ex_aeg_larvas||0)+(r.lab_ex_aeg_pupas||0)+(r.lab_ex_aeg_exuvias||0)+(r.lab_ex_aeg_adultos||0) || '—'}
                          </td>
                        </tr>
                        <tr style={rowStyle}>
                          <td style={{ ...tdV, textAlign: 'left', fontStyle: 'italic', color: '#92400E' }}>Ae. albopictus</td>
                          {[r.lab_ex_alb_larvas,r.lab_ex_alb_pupas,r.lab_ex_alb_exuvias,r.lab_ex_alb_adultos].map((v,i) => (
                            <td key={i} style={tdV}>{fmtV(v)}</td>
                          ))}
                          <td style={{ ...tdV, fontWeight: 800, color: '#92400E' }}>
                            {(r.lab_ex_alb_larvas||0)+(r.lab_ex_alb_pupas||0)+(r.lab_ex_alb_exuvias||0)+(r.lab_ex_alb_adultos||0) || '—'}
                          </td>
                        </tr>
                        <tr style={rowStyle}>
                          <td style={{ ...tdV, textAlign: 'left', fontStyle: 'italic', color: '#374151' }}>Outros</td>
                          {[r.lab_ex_outros_larvas,r.lab_ex_outros_pupas,r.lab_ex_outros_exuvias,r.lab_ex_outros_adultos].map((v,i) => (
                            <td key={i} style={tdV}>{fmtV(v)}</td>
                          ))}
                          <td style={{ ...tdV, fontWeight: 800, color: '#374151' }}>
                            {(r.lab_ex_outros_larvas||0)+(r.lab_ex_outros_pupas||0)+(r.lab_ex_outros_exuvias||0)+(r.lab_ex_outros_adultos||0) || '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>
                {(r.lab_data_processamento || r.lab_agente_analise || r.lab_assinatura) && (
                  <Box sx={{ ...panelSx, mb: 0, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {r.lab_data_processamento && (
                      <Box>
                        <BlocoTitulo>Data de Processamento</BlocoTitulo>
                        <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>
                          {new Date(r.lab_data_processamento + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </Typography>
                      </Box>
                    )}
                    {r.lab_agente_analise && (
                      <Box>
                        <BlocoTitulo>Agente que Analisou</BlocoTitulo>
                        <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>{r.lab_agente_analise}</Typography>
                      </Box>
                    )}
                    {r.lab_assinatura && (
                      <Box>
                        <BlocoTitulo>Assinatura do Laboratório</BlocoTitulo>
                        <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>{r.lab_assinatura}</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Collapse>
          </Card>
        </Box>
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════
  // FORMULÁRIO
  // ════════════════════════════════════════════════════════
  if (vista === 'form') {
    const cardStyle = { borderRadius: 2, border: '1px solid #BFDBFE', mb: 2 };
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F4FF', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #0F2347, #1B3A6B, #2D5A9E)' }}>
          <Toolbar>
            <IconButton color="inherit" onClick={() => setVista('lista')} sx={{ mr: 1 }}><ArrowBack /></IconButton>
            <Assignment sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
              {editandoId ? 'Editar FAD-01' : 'Novo FAD-01'}
            </Typography>
            <Button variant="contained" onClick={() => salvar()} disabled={salvando}
              sx={{ bgcolor: '#065F46', '&:hover': { bgcolor: '#047857' }, fontWeight: 700, borderRadius: 2 }}>
              {salvando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : editandoId ? 'Atualizar' : 'Salvar FAD-01'}
            </Button>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 960, mx: 'auto', width: '100%', flexGrow: 1 }}>
          {aviso  && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAviso('')}>{aviso}</Alert>}
          {erro && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setErro('')}>{erro}</Alert>}

          {/* ── CABEÇALHO ── */}
          <Card sx={{ ...cardStyle }}>
            <SecaoHeader titulo="Identificação do Dia" cor="#1B3A6B" />
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                <TextField label="Data" type="date" size="small" value={form.data} required sx={{ width: 150 }}
                  onChange={e => setForm((f: any) => ({ ...f, data: e.target.value }))}
                  slotProps={{ inputLabel: { shrink: true } }} />
                <TextField select label="Ciclo" size="small" value={form.ciclo_id} sx={{ width: 180 }} required
                  onChange={e => setForm((f: any) => ({ ...f, ciclo_id: e.target.value }))}>
                  {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}{c.ativo ? ' ●' : ''}</MenuItem>)}
                </TextField>
                <TextField select label="Agente" size="small" value={form.agente_id} sx={{ minWidth: 220 }} required
                  onChange={e => setForm((f: any) => ({ ...f, agente_id: e.target.value }))}>
                  {agentes.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome} — {a.matricula}</MenuItem>)}
                </TextField>
                <TextField select label="Área" size="small" value={form.area_id} sx={{ minWidth: 180 }} required
                  onChange={e => setForm((f: any) => ({ ...f, area_id: e.target.value }))}>
                  {areas.map(a => <MenuItem key={a.id} value={String(a.id)}>{a.nome} ({a.zona})</MenuItem>)}
                </TextField>
                <TextField select label="Atividade" size="small" value={form.atividade} sx={{ width: 120 }}
                  onChange={e => setForm((f: any) => ({ ...f, atividade: e.target.value }))}>
                  {ATIVIDADES.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </TextField>
                <FormControlLabel
                  control={<Checkbox checked={form.eh_pe}
                    onChange={e => {
                      const checked = e.target.checked;
                      setForm((f: any) => ({
                        ...f,
                        eh_pe: checked,
                        ciclo_pe_id: checked ? f.ciclo_pe_id : null,
                        imoveis_residencia:    checked ? 0 : f.imoveis_residencia,
                        imoveis_comercio:      checked ? 0 : f.imoveis_comercio,
                        imoveis_terreno_baldio: checked ? 0 : f.imoveis_terreno_baldio,
                        imoveis_outros:        checked ? 0 : f.imoveis_outros,
                      }));
                    }}
                    size="small" sx={{ color: '#5856D6', '&.Mui-checked': { color: '#5856D6' } }} />}
                  label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: form.eh_pe ? '#5856D6' : '#6B7280' }}>
                    Ponto Estratégico (PE)
                  </Typography>}
                />
                {form.eh_pe && (
                  <TextField select label="Ciclo PE" size="small" value={form.ciclo_pe_id ?? ''} sx={{ width: 160 }} required
                    onChange={e => setForm((f: any) => ({ ...f, ciclo_pe_id: e.target.value }))}>
                    {ciclosPe
                      .filter(c => c.ativo || String(c.id) === String(form.ciclo_pe_id))
                      .map(c => <MenuItem key={c.id} value={String(c.id)}>PE {c.numero}/{c.ano}{c.ativo ? ' ●' : ''}</MenuItem>)}
                  </TextField>
                )}
                {form.eh_pe && (
                  <Button variant="outlined" size="small" disabled={criandoPe}
                    onClick={criarNovoCicloPe}
                    sx={{ height: 40, textTransform: 'none', fontWeight: 600,
                      color: '#5856D6', borderColor: '#5856D6',
                      '&:hover': { borderColor: '#5856D6', bgcolor: 'rgba(88,86,214,0.06)' } }}>
                    {criandoPe ? 'Criando…' : '+ Novo PE'}
                  </Button>
                )}
                {form.eh_pe && (
                  <Box sx={{
                    width: '100%', mt: 0.5, p: 1.2, borderRadius: 1.5,
                    bgcolor: 'rgba(88, 86, 214, 0.08)', border: '1px solid rgba(88, 86, 214, 0.3)',
                    display: 'flex', alignItems: 'center',
                  }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#5856D6', fontWeight: 600 }}>
                      ⚠ Lançamento PE — Este FAD-01 será exclusivo do módulo Ponto Estratégico. Não alimentará Resumo Semanal, Resumo Geral, Pendências ou FAD-07. Apenas o campo "P. Estratégico" pode ter valor.
                    </Typography>
                  </Box>
                )}
                <FormControlLabel
                  control={<Switch checked={form.concluida}
                    onChange={e => setForm((f: any) => ({ ...f, concluida: e.target.checked }))}
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: form.concluida ? '#065F46' : '#9CA3AF' },
                      '& .MuiSwitch-track': { bgcolor: form.concluida ? '#065F4680' : '#D1D5DB' } }} />}
                  label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: form.concluida ? '#065F46' : '#6B7280' }}>
                    {form.concluida ? 'Concluída' : 'Pendente'}
                  </Typography>}
                />
              </Box>
            </CardContent>
          </Card>

          {/* ── SEÇÃO 1 ── */}
          <Card sx={{ ...cardStyle }}>
            <SecaoHeader titulo="Seção 1 — Resumo do Trabalho de Campo" cor="#1B3A6B" />
            <CardContent sx={{ p: 2 }}>

              {/* Bloco A */}
              <BlocoTitulo>A — Nº Imóveis trabalhados por tipo</BlocoTitulo>
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr>
                    {['Residência','Comércio','T. Baldio','P. Estratégico','Outra','TOTAL'].map(h => (
                      <th key={h} style={TH_STYLE}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody><tr>
                    <NumCell f="imoveis_residencia"     form={form} setForm={setForm} disabled={form.eh_pe} />
                    <NumCell f="imoveis_comercio"       form={form} setForm={setForm} disabled={form.eh_pe} />
                    <NumCell f="imoveis_terreno_baldio" form={form} setForm={setForm} disabled={form.eh_pe} />
                    <NumCell f="imoveis_ponto_estrategico" form={form} setForm={setForm} />
                    <NumCell f="imoveis_outros"         form={form} setForm={setForm} disabled={form.eh_pe} />
                    <TotalCell value={totImov} />
                  </tr></tbody>
                </table>
              </Box>

              {/* Bloco B */}
              <BlocoTitulo>B — Nº Imóveis (situação)</BlocoTitulo>
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr>
                    {['Trat. Focal','Trat. Perifocal','Inspecionados','Recuperados','Recusa','Fechados'].map(h => (
                      <th key={h} style={TH_STYLE}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody><tr>
                    <NumCell f="trat_focal" form={form} setForm={setForm} color="#065F46" />
                    <NumCell f="trat_perifocal" form={form} setForm={setForm} color="#065F46" />
                    <NumCell f="inspecionados" form={form} setForm={setForm} color="#065F46" />
                    <NumCell f="recuperados" form={form} setForm={setForm} />
                    <NumCell f="recusados" form={form} setForm={setForm} />
                    <NumCell f="fechados" form={form} setForm={setForm} />
                  </tr></tbody>
                </table>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!form.gerar_pendencias}
                      onChange={e => setForm((f: any) => ({ ...f, gerar_pendencias: !e.target.checked }))}
                      size="small"
                    />
                  }
                  label="Não gerar pendências (lançamento consolidado/retroativo)"
                  sx={{ mt: 2, color: '#6B7280', fontSize: 13 }}
                />
              </Box>

              {/* Bloco C / D */}
              <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <Box>
                  <BlocoTitulo>C — Tubitos / Amostras Coletadas</BlocoTitulo>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr><th style={TH_STYLE}>Nº Amostras</th></tr></thead>
                    <tbody><tr><NumCell f="amostras_coletadas" form={form} setForm={setForm} color="#065F46" /></tr></tbody>
                  </table>
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                  <BlocoTitulo>D — Pendência</BlocoTitulo>
                  <TextField fullWidth size="small" value={form.pendencia || ''}
                    placeholder="Descreva pendências do dia (opcional)"
                    onChange={e => setForm((f: any) => ({ ...f, pendencia: e.target.value }))} />
                </Box>
              </Box>

              {/* Bloco E */}
              <BlocoTitulo>E — Nº Depósitos inspecionados por tipo</BlocoTitulo>
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr>
                    {['A1','A2','B','C','D1','D2','E','TOTAL'].map(h => (
                      <th key={h} style={TH_STYLE}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody><tr>
                    <NumCell f="dep_a1" form={form} setForm={setForm} color="#92400E" />
                    <NumCell f="dep_a2" form={form} setForm={setForm} color="#92400E" />
                    <NumCell f="dep_b"  form={form} setForm={setForm} color="#92400E" />
                    <NumCell f="dep_c"  form={form} setForm={setForm} color="#92400E" />
                    <NumCell f="dep_d1" form={form} setForm={setForm} color="#92400E" />
                    <NumCell f="dep_d2" form={form} setForm={setForm} color="#92400E" />
                    <NumCell f="dep_e"  form={form} setForm={setForm} color="#92400E" />
                    <TotalCell value={totDep} color="#92400E" />
                  </tr></tbody>
                </table>
              </Box>

              {/* Bloco F */}
              <BlocoTitulo>F — Depósitos (resultado do tratamento)</BlocoTitulo>
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={TH_STYLE}>Eliminados</th>
                    <th style={TH_STYLE}>Tratados</th>
                  </tr></thead>
                  <tbody><tr>
                    <NumCell f="dep_eliminados" form={form} setForm={setForm} />
                    <NumCell f="dep_tratados"   form={form} setForm={setForm} />
                  </tr></tbody>
                </table>
              </Box>

              {/* Bloco G / H — side by side */}
              <BlocoTitulo>G / H — Larvicida</BlocoTitulo>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                {[
                  { lbl: 'Larvicida 1 (G)', tipo: 'larvicida_l1_tipo', g: 'larvicida_l1_qtd_gramas', dep: 'larvicida_l1_qtd_depositos' },
                  { lbl: 'Larvicida 2 (H)', tipo: 'larvicida_l2_tipo', g: 'larvicida_l2_qtd_gramas', dep: 'larvicida_l2_qtd_depositos' },
                ].map(({ lbl, tipo, g, dep }) => (
                  <Box key={lbl} sx={{ overflowX: 'auto' }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#4C1D95', mb: 0.4 }}>{lbl}</Typography>
                    <table style={{ borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <th style={{ ...TH_STYLE, minWidth: 190 }}>Tipo</th>
                        <th style={{ ...TH_STYLE, minWidth: 90 }}>Qtde (g)</th>
                        <th style={{ ...TH_STYLE, minWidth: 80 }}>Dep. Trat.</th>
                      </tr></thead>
                      <tbody><tr>
                        <SelectCell f={tipo} form={form} setForm={setForm} opts={LARVICIDAS} />
                        <td style={TD_STYLE}>
                          <input type="number" min={0} step={0.1}
                            value={form[g] || ''}
                            style={{ ...INPUT_STYLE, color: '#4C1D95', minWidth: 80 }}
                            placeholder="0,0"
                            onChange={e => setForm((f: any) => comUsoLarvicida(f, tipo, g, Number(e.target.value) || 0))}
                          />
                        </td>
                        <td style={TD_STYLE}>
                          <input type="number" min={0} value={form[dep] || ''} placeholder="0"
                            style={{ ...INPUT_STYLE, color: '#4C1D95', opacity: 1, cursor: 'text' }}
                            onChange={e => setForm((f: any) => comUsoLarvicida(f, tipo, dep, Number(e.target.value) || 0))}
                          />
                        </td>
                      </tr></tbody>
                    </table>
                  </Box>
                ))}
              </Box>

              {/* Bloco I */}
              <BlocoTitulo>I — Adulticida</BlocoTitulo>
              <Box sx={{ overflowX: 'auto', mb: 2 }}>
                <table style={{ borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...TH_STYLE, minWidth: 190 }}>Tipo</th>
                    <th style={{ ...TH_STYLE, minWidth: 100 }}>Qtde (cargas)</th>
                  </tr></thead>
                  <tbody><tr>
                    <SelectCell f="adulticida_tipo" form={form} setForm={setForm} opts={ADULTICIDAS} />
                    <td style={TD_STYLE}>
                      <input type="number" min={0} step={1}
                        value={form.adulticida_cargas ?? ''}
                        disabled={!form.adulticida_tipo}
                        style={{ ...INPUT_STYLE, minWidth: 90 }}
                        placeholder="0"
                        onChange={e => setForm((f: any) => ({ ...f, adulticida_cargas: e.target.value === '' ? null : Number(e.target.value) }))}
                      />
                    </td>
                  </tr></tbody>
                </table>
              </Box>

              {/* Bloco J — Quarteirões Trabalhados */}
              <BlocoTitulo>J — Quarteirões Trabalhados (grade 30)</BlocoTitulo>
              <Box sx={{ mb: 2 }}>
                <GradeQuarteirao value={form.quarteiroes_trabalhados || emptyGrid(30)}
                  onChange={v => setForm((f: any) => ({ ...f, quarteiroes_trabalhados: v }))}
                  rows={3} cols={10} />
              </Box>

              {/* Bloco K — Quarteirões Concluídos */}
              <BlocoTitulo>K — Quarteirões Concluídos (grade 30)</BlocoTitulo>
              <Box sx={{ mb: 1 }}>
                <GradeQuarteirao value={form.quarteiroes_concluidos || emptyGrid(30)}
                  onChange={v => setForm((f: any) => ({ ...f, quarteiroes_concluidos: v }))}
                  rows={3} cols={10} />
              </Box>
              {/* Campo texto AH — Quarteirões Concluídos (via planilha) */}
              <Box sx={{ mt: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#065F46', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Concluídos via planilha (coluna AH)
                </Typography>
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 1.5 }}>
                  <Typography sx={{ fontSize: '0.85rem', color: '#065F46', fontWeight: 600 }}>
                    {form.quarteirao_concluidos_texto?.trim()
                      ? form.quarteirao_concluidos_texto
                      : <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Nenhum quarteirão concluído registrado via planilha</span>}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* ── SEÇÃO 2 (LABORATÓRIO) ── */}
          <Card sx={{ borderRadius: 2, border: `1px solid ${labAberto ? '#A7F3D0' : '#E5E7EB'}`, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', px: 2, py: 1.5,
              background: labAberto ? '#D1FAE520' : '#F9FAFB',
              borderRadius: labAberto ? '8px 8px 0 0' : 2 }}
              onClick={() => setLabAberto(v => !v)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Science sx={{ fontSize: 18, color: labAberto ? '#065F46' : '#9CA3AF' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', textTransform: 'uppercase',
                  letterSpacing: '0.1em', color: labAberto ? '#065F46' : '#6B7280' }}>
                  Seção 2 — Resumo do Laboratório (preencher depois)
                </Typography>
              </Box>
              {labAberto ? <ExpandLess sx={{ color: '#065F46' }} /> : <ExpandMore sx={{ color: '#9CA3AF' }} />}
            </Box>
            <Collapse in={labAberto}>
              <CardContent sx={{ p: 2 }}>
                <CampoEtiquetaTubitos fad01Id={editandoId} qtdTela={form.amostras_coletadas} />

                {/* Bloco L / M — grades 20 */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 1 }}>
                  <Box>
                    <BlocoTitulo>L — Quarteirões com Ae. aegypti (grade 20)</BlocoTitulo>
                    <GradeQuarteirao value={form.lab_quart_aegypti || emptyGrid(20)}
                      onChange={v => setForm((f: any) => ({ ...f, lab_quart_aegypti: v }))}
                      rows={2} cols={10} />
                  </Box>
                  <Box>
                    <BlocoTitulo>M — Quarteirões com Ae. albopictus (grade 20)</BlocoTitulo>
                    <GradeQuarteirao value={form.lab_quart_albopictus || emptyGrid(20)}
                      onChange={v => setForm((f: any) => ({ ...f, lab_quart_albopictus: v }))}
                      rows={2} cols={10} />
                  </Box>
                </Box>

                {/* Bloco N — depósitos com espécimes */}
                <BlocoTitulo>N — Nº Depósitos com espécimes por tipo</BlocoTitulo>
                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={{ ...TH_STYLE, textAlign: 'left', minWidth: 130 }}>Espécie</th>
                      {['A1','A2','B','C','D1','D2','E','TOTAL'].map(h => (
                        <th key={h} style={TH_STYLE}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      <tr>
                        <td style={{ border: '1px solid #D1D5DB', padding: '5px 8px', fontSize: '0.78rem',
                          fontStyle: 'italic', color: '#991B1B', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          Ae. aegypti
                        </td>
                        <NumCellNull f="lab_dep_aeg_a1" form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_aeg_a2" form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_aeg_b"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_aeg_c"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_aeg_d1" form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_aeg_d2" form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_aeg_e"  form={form} setForm={setForm} />
                        <TotalCell value={totLabDepAeg} color="#991B1B" />
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #D1D5DB', padding: '5px 8px', fontSize: '0.78rem',
                          fontStyle: 'italic', color: '#92400E', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          Ae. albopictus
                        </td>
                        <NumCellNull f="lab_dep_alb_a1" form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_alb_a2" form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_alb_b"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_alb_c"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_alb_d1" form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_alb_d2" form={form} setForm={setForm} />
                        <NumCellNull f="lab_dep_alb_e"  form={form} setForm={setForm} />
                        <TotalCell value={totLabDepAlb} color="#92400E" />
                      </tr>
                    </tbody>
                  </table>
                </Box>

                {/* Bloco O — imóveis com espécimes */}
                <BlocoTitulo>O — Nº Imóveis com espécimes por tipo</BlocoTitulo>
                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={{ ...TH_STYLE, textAlign: 'left', minWidth: 130 }}>Espécie</th>
                      {['R','C','TB','PE','O','TOTAL'].map(h => (
                        <th key={h} style={TH_STYLE}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      <tr>
                        <td style={{ border: '1px solid #D1D5DB', padding: '5px 8px', fontSize: '0.78rem',
                          fontStyle: 'italic', color: '#991B1B', fontWeight: 600 }}>Ae. aegypti</td>
                        <NumCellNull f="lab_imov_aeg_r"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_aeg_c"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_aeg_tb" form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_aeg_pe" form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_aeg_o"  form={form} setForm={setForm} />
                        <TotalCell value={totLabImovAeg} color="#991B1B" />
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #D1D5DB', padding: '5px 8px', fontSize: '0.78rem',
                          fontStyle: 'italic', color: '#92400E', fontWeight: 600 }}>Ae. albopictus</td>
                        <NumCellNull f="lab_imov_alb_r"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_alb_c"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_alb_tb" form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_alb_pe" form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_alb_o"  form={form} setForm={setForm} />
                        <TotalCell value={totLabImovAlb} color="#92400E" />
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #D1D5DB', padding: '5px 8px', fontSize: '0.78rem',
                          fontStyle: 'italic', color: '#374151', fontWeight: 600 }}>Outros</td>
                        <NumCellNull f="lab_imov_outros_r"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_outros_c"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_outros_tb" form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_outros_pe" form={form} setForm={setForm} />
                        <NumCellNull f="lab_imov_outros_o"  form={form} setForm={setForm} />
                        <TotalCell value={(form.lab_imov_outros_r||0)+(form.lab_imov_outros_c||0)+(form.lab_imov_outros_tb||0)+(form.lab_imov_outros_pe||0)+(form.lab_imov_outros_o||0)} color="#374151" />
                      </tr>
                    </tbody>
                  </table>
                </Box>

                {/* Bloco P — Nº Exemplares */}
                <BlocoTitulo>P — Nº de Exemplares</BlocoTitulo>
                <Box sx={{ overflowX: 'auto', mb: 2 }}>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={{ ...TH_STYLE, textAlign: 'left', minWidth: 130 }}>Espécie</th>
                      {['Larvas','Pupas','Exúvias','Adultos','TOTAL'].map(h => (
                        <th key={h} style={TH_STYLE}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      <tr>
                        <td style={{ border: '1px solid #D1D5DB', padding: '5px 8px', fontSize: '0.78rem',
                          fontStyle: 'italic', color: '#991B1B', fontWeight: 600 }}>Ae. aegypti</td>
                        <NumCellNull f="lab_ex_aeg_larvas"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_aeg_pupas"   form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_aeg_exuvias" form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_aeg_adultos" form={form} setForm={setForm} />
                        <TotalCell value={totExAeg} color="#991B1B" />
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #D1D5DB', padding: '5px 8px', fontSize: '0.78rem',
                          fontStyle: 'italic', color: '#92400E', fontWeight: 600 }}>Ae. albopictus</td>
                        <NumCellNull f="lab_ex_alb_larvas"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_alb_pupas"   form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_alb_exuvias" form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_alb_adultos" form={form} setForm={setForm} />
                        <TotalCell value={totExAlb} color="#92400E" />
                      </tr>
                      <tr>
                        <td style={{ border: '1px solid #D1D5DB', padding: '5px 8px', fontSize: '0.78rem',
                          fontStyle: 'italic', color: '#374151', fontWeight: 600 }}>Outros</td>
                        <NumCellNull f="lab_ex_outros_larvas"  form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_outros_pupas"   form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_outros_exuvias" form={form} setForm={setForm} />
                        <NumCellNull f="lab_ex_outros_adultos" form={form} setForm={setForm} />
                        <TotalCell value={totExOutros} color="#374151" />
                      </tr>
                    </tbody>
                  </table>
                </Box>

                {/* Linha: Data de Processamento + Agente que Analisou + Assinatura */}
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-end', mt: 1 }}>
                  <Box>
                    <BlocoTitulo>Data de Processamento</BlocoTitulo>
                    <TextField size="small" type="date" value={form.lab_data_processamento || ''} sx={{ width: 180 }}
                      onChange={e => setForm((f: any) => ({ ...f, lab_data_processamento: e.target.value }))} />
                  </Box>
                  <Box>
                    <BlocoTitulo>Agente que Analisou</BlocoTitulo>
                    <TextField size="small" value={form.lab_agente_analise || ''} sx={{ width: 250 }}
                      placeholder="Ex: Adriana"
                      onChange={e => setForm((f: any) => ({ ...f, lab_agente_analise: e.target.value }))} />
                  </Box>
                  <Box>
                    <BlocoTitulo>Assinatura do Laboratório</BlocoTitulo>
                    <TextField size="small" value={form.lab_assinatura || ''} sx={{ width: 250 }}
                      placeholder="Ex: VL-Ass"
                      onChange={e => setForm((f: any) => ({ ...f, lab_assinatura: e.target.value }))} />
                  </Box>
                </Box>
              </CardContent>
            </Collapse>
            {/* Conferência do laboratório (Sessão 29) — visível mesmo com a seção recolhida */}
            {!form.eh_pe && (
              <Box ref={conferenciaRef}>
                <ConferenciaLaboratorio resultado={conferencia} falhou={conferenciaFalhou}
                  conferindo={conferindo} temLaboratorio={labNoForm(form)} />
              </Box>
            )}
          </Card>

          <Dialog open={!!avisosSalvar} onClose={() => setAvisosSalvar(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Atenção — confira o laboratório</DialogTitle>
            <DialogContent>
              <Alert severity="warning">
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {(avisosSalvar?.avisos ?? []).map((a, i) => <li key={i}>{textoItens(a)}</li>)}
                </Box>
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAvisosSalvar(null)}>Voltar e corrigir</Button>
              <Button variant="contained" color="warning"
                onClick={() => { setAvisosSalvar(null); salvar(true); }}>
                Salvar mesmo assim
              </Button>
            </DialogActions>
          </Dialog>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 4 }}>
            <Button onClick={() => setVista('lista')} sx={{ color: '#6B7280' }}>Cancelar</Button>
            <Button variant="contained" onClick={() => salvar()} disabled={salvando}
              sx={{ bgcolor: '#1B3A6B', '&:hover': { bgcolor: '#152d52' }, fontWeight: 700, px: 4, borderRadius: 2 }}>
              {salvando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : editandoId ? 'Atualizar FAD-01' : 'Salvar FAD-01'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // ════════════════════════════════════════════════════════
  // LISTA
  // ════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F4FF', display: 'flex', flexDirection: 'column' }}>
      <ModuleHeader
        titulo="FAD-01"
        subtitulo="Registro diário de produção"
        icone={<DescriptionIcon sx={{ fontSize: 32 }} />}
        accentColor="#E65100"
        onVoltar={onVoltar}
        chips={[{ label: 'Hoje', value: hoje, color: '#00b8cc', sub: new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) }]}
      />

      <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto', width: '100%', flexGrow: 1 }}>
        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
        {aviso   && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAviso('')}>{aviso}</Alert>}
        {erro    && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

        <Card sx={{ borderRadius: 3, mb: 3, border: '1px solid #E5E7EB' }}>
          <CardContent sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FilterList sx={{ color: '#9CA3AF' }} />
            <TextField select label="Filtrar por ciclo" value={filtroCiclo} size="small" sx={{ minWidth: 220 }}
              onChange={e => setFiltroCiclo(e.target.value)}>
              <MenuItem value="">Todos os ciclos</MenuItem>
              {ciclos.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.nome}{c.ativo ? ' (ativo)' : ''}</MenuItem>)}
            </TextField>
            <TextField select label="Agente" value={filtroAgente} size="small" sx={{ minWidth: 220 }}
              onChange={e => setFiltroAgente(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Array.from(new Set(registros.map(r => r.agente.nome))).sort().map(nome => (
                <MenuItem key={nome} value={nome}>{nome}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Área" value={filtroArea} size="small" sx={{ minWidth: 220 }}
              onChange={e => setFiltroArea(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {Array.from(new Set(registros.map(r => r.area.nome))).sort().map(nome => (
                <MenuItem key={nome} value={nome}>{nome}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Semana Epi." value={filtroSE ?? ''} size="small" sx={{ minWidth: 220 }}
              onChange={e => setFiltroSE(e.target.value === '' ? null : Number(e.target.value))}>
              <MenuItem value="">Todas</MenuItem>
              {Array.from(new Set(registros.map(r => getSE(r.data)))).sort((a, b) => a - b).map(se => (
                <MenuItem key={se} value={se}>{se}ª</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" startIcon={<Add />} onClick={abrirNovo}
              sx={{ ml: 'auto', bgcolor: '#065F46', '&:hover': { bgcolor: '#047857' }, fontWeight: 700, borderRadius: 2 }}>
              + Novo FAD-01
            </Button>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              {registrosFiltrados.length} registro{registrosFiltrados.length !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>

        {registrosFiltrados.length === 0 ? (
          <Card sx={{ borderRadius: 3, textAlign: 'center', p: 8, border: '2px dashed #E5E7EB' }}>
            <Assignment sx={{ fontSize: 64, color: '#D1D5DB', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#6B7280' }}>Nenhum lançamento encontrado</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={abrirNovo}
              sx={{ bgcolor: '#1B3A6B', fontWeight: 700, mt: 3 }}>+ Novo FAD-01</Button>
          </Card>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Data','Ciclo','Agente','Área','Atividade','Quart.','Imóveis','Depósitos','Ações'].map(h => (
                    <TableCell key={h} sx={{
                      backgroundColor: '#1D1D1F',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      textAlign: 'center',
                    }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {registrosFiltrados.map((r, idx) => {
                  const totIm = totalImoveis(r);
                  const totDp = totalDep(r);
                  return (
                    <TableRow key={r.id} sx={{
                      '&:hover .MuiTableCell-root': { filter: 'brightness(0.95)', transition: 'filter 0.15s' },
                    }}>
                      <TableCell sx={{ backgroundColor: '#FFFFFF', fontWeight: 700, color: '#1D1D1F', fontSize: 13 }}>{new Date(r.data + 'T12:00:00').toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell sx={{ backgroundColor: '#F7F7F8', textAlign: 'center' }}>{r.ciclo.nome}</TableCell>
                      <TableCell sx={{ backgroundColor: '#FFFFFF', fontWeight: 700, color: '#1D1D1F', fontSize: 13 }}>{r.agente.nome}</TableCell>
                      <TableCell sx={{ backgroundColor: '#F7F7F8', textAlign: 'center' }}>{r.area.nome}</TableCell>
                      <TableCell sx={{ backgroundColor: '#FFFFFF', textAlign: 'center' }}>{r.atividade}</TableCell>
                      <TableCell sx={{ backgroundColor: '#F7F7F8', textAlign: 'center' }}>{r.quarteirao_texto || '—'}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', backgroundColor: '#EFEFF2', fontWeight: 600, textAlign: 'center' }}>{totIm}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', backgroundColor: '#F7F7F8', fontWeight: 600, textAlign: 'center' }}>{totDp}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', backgroundColor: '#FFFFFF', textAlign: 'center' }}>
                        <IconButton size="small" onClick={() => abrirVisualizar(r)} sx={{ color: '#0071E3' }}>
                          <Visibility fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => abrirEditar(r)} sx={{ color: '#34C759' }}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setConfirmarDelete(r.id)} sx={{ color: '#FF3B30' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box sx={{ py: 2, textAlign: 'center', borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
          © 2026 Tovis · <strong style={{ color: '#6B7280' }}>Walber Reis</strong>
        </Typography>
      </Box>

      <Dialog open={confirmarDelete !== null} onClose={() => setConfirmarDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#991B1B', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning /> Confirmar exclusão
        </DialogTitle>
        <DialogContent>
          <Typography>Deseja remover este lançamento FAD-01? Esta ação não pode ser desfeita.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmarDelete(null)} startIcon={<Close />} sx={{ color: '#6B7280' }}>Cancelar</Button>
          <Button variant="contained" color="error"
            onClick={() => confirmarDelete && deletar(confirmarDelete)}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
